import {
  type CustomerOption,
  type LevelDiscounts,
  NO_LEVEL_DISCOUNTS,
  type PriceLevel,
  type PricingSettings,
  type ResolvedPrice,
  resolvePrice,
  type SaleUnit,
  type VariantPricesResult,
  type VariantPricing,
} from '@sahab/contracts'
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { Language } from '../../lib/format'
import { settingGroupQueryOptions } from '../settings/queries'

/**
 * Price levels and unit packs on the web (ADR-045): what a document needs to price its lines for its
 * customer, with the same `resolvePrice` the API records the level with.
 */

export const pricingKeys = {
  all: ['pricing'] as const,
  level: (customerId: string | null) => [...pricingKeys.all, 'level', customerId] as const,
  variants: (ids: readonly string[], customerId: string | null, date: string | null) =>
    [...pricingKeys.all, 'variants', [...ids].sort().join(','), customerId, date] as const,
}

/** The level a customer buys at, through the picker endpoint (document creators may call it). */
export function customerLevelQueryOptions(customerId: string | null) {
  return queryOptions({
    queryKey: pricingKeys.level(customerId),
    queryFn: async ({ signal }): Promise<PriceLevel> => {
      if (!customerId) return 'RETAIL'
      const [found] = await api.get<CustomerOption[]>('/customers/options', {
        signal,
        query: { id: customerId },
      })
      return found?.priceLevel ?? 'RETAIL'
    },
    staleTime: 60_000,
  })
}

/** GET /product-variants/prices: known variants priced for the customer on the date. */
export function variantPricesQueryOptions(
  variantIds: readonly string[],
  customerId: string | null,
  date: string | null,
) {
  const ids = [...new Set(variantIds)].sort()
  return queryOptions({
    queryKey: pricingKeys.variants(ids, customerId, date),
    queryFn: ({ signal }) =>
      api.get<VariantPricesResult>('/product-variants/prices', {
        signal,
        query: {
          variantIds: ids.join(','),
          ...(customerId ? { customerId } : {}),
          ...(date ? { date } : {}),
        },
      }),
    enabled: ids.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

/** The level discounts the `pricing` setting group holds (none while it is unknown). */
export function levelDiscountsOf(value: PricingSettings | undefined): LevelDiscounts {
  return value
    ? { WHOLESALE: value.wholesaleDiscount, DISTRIBUTOR: value.distributorDiscount }
    : NO_LEVEL_DISCOUNTS
}

/** The company's level discounts (setting group `pricing`, readable by anyone signed in). */
export function useLevelDiscounts(): LevelDiscounts {
  return levelDiscountsOf(useQuery(settingGroupQueryOptions('pricing')).data?.value)
}

export interface DocumentPricing {
  /** The customer's level (Retail without a customer, or until it is known). */
  level: PriceLevel
  discounts: LevelDiscounts
  /** The variant's units, grid and the customer's custom prices, once loaded. */
  pricingOf: (variantId: string) => VariantPricing | undefined
  /** True while the prices of the current customer, date and variants are loaded and current. */
  settled: boolean
}

/**
 * Everything a document form needs to price product lines: the customer's level, the level discounts and
 * each variant's pricing for the customer on the date (re-read when any of them changes).
 */
export function useDocumentPricing(
  customerId: string | null,
  date: string | null,
  variantIds: readonly string[],
): DocumentPricing {
  const level = useQuery(customerLevelQueryOptions(customerId))
  const discounts = useLevelDiscounts()
  const prices = useQuery(variantPricesQueryOptions(variantIds, customerId, date))
  const byId = new Map((prices.data?.items ?? []).map((p) => [p.variantId, p]))
  return {
    level: level.data ?? 'RETAIL',
    discounts,
    pricingOf: (variantId) => byId.get(variantId),
    settled: level.isSuccess && (variantIds.length === 0 || (prices.isSuccess && !prices.isPlaceholderData)),
  }
}

/** The price of a unit (null: the base unit) for the level, or null when the unit is not the variant's. */
export function priceIn(
  pricing: Partial<VariantPricing> | undefined,
  unitId: string | null,
  level: PriceLevel,
  discounts: LevelDiscounts,
): ResolvedPrice | null {
  if (!pricing?.units?.length || !pricing.prices) return null
  const unit = unitId ?? pricing.units.find((u) => u.isBase)?.unitId
  if (!unit) return null
  return resolvePrice(
    { units: pricing.units, prices: pricing.prices, customPrices: pricing.customPrices ?? [] },
    unit,
    level,
    discounts,
  )
}

/** A unit's symbol in the UI language. */
export function unitSymbol(unit: { symbol: string; symbolAr: string | null }, language: Language): string {
  return language === 'ar' ? unit.symbolAr?.trim() || unit.symbol : unit.symbol
}

/** A unit's name in the UI language. */
export function unitName(unit: { name: string; nameAr: string | null }, language: Language): string {
  return language === 'ar' ? unit.nameAr?.trim() || unit.name : unit.name
}

/** What a unit choice shows: the symbol, and for a pack how many base units it holds ("ctn ×12"). */
export function unitChoiceLabel(unit: SaleUnit, language: Language): string {
  const symbol = unitSymbol(unit, language)
  return unit.isBase ? symbol : `${symbol} ×${unit.factor}`
}
